// src/logic/helpers/getClientTz.ts
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

export type TzSource = "intl" | "temporal" | "dayjs-guess" | "ip" | "default";

export interface ClientTz {
    /** Always an IANA TZDB identifier, e.g. "Europe/Amsterdam" */
    tz: string;
    source: TzSource;
}

export async function getClientTz(): Promise<ClientTz> {
    /* 1️⃣ Native Intl (evergreen browsers) */
    if (typeof Intl !== "undefined" && Intl.DateTimeFormat) {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz) return { tz, source: "intl" };
    }

    /* 2️⃣ Temporal API (polyfilled or native in future Chrome/Safari/Firefox) */
    if (globalThis.Temporal?.Now?.timeZoneId) {
        return { tz: globalThis.Temporal.Now.timeZoneId(), source: "temporal" };
    }

    /* 3️⃣ Heuristic guess for ancient browsers */
    const guessedTz = dayjs.tz.guess();
    if (guessedTz) {
        return { tz: guessedTz, source: "dayjs-guess" };
    }

    /* 4️⃣ Geo-IP fallback (still IANA strings) */
    try {
        const { timezone } = await fetch("https://ipapi.co/json").then((r) =>
            r.json(),
        );
        if (timezone) return { tz: timezone as string, source: "ip" };
    } catch {
        /* network failure – ignore */
    }

    /* 5️⃣ Last-ditch default */
    return { tz: "Etc/UTC", source: "default" };
}
